// Config de navegação da UI mobile (tab bar + bottom sheet "Mais" + launcher v2).
// Separado do NAV_ITEMS do desktop pra poder ter ícones e agrupamento próprios
// sem tocar no layout desktop.
import type { ComponentType } from 'react'
import {
  IconGrid, IconChart, IconPackage, IconSparkles, IconReceipt,
  IconTruck, IconInbox, IconTag, IconArchive, IconBuilding, IconSettings, IconHome, type IconProps,
} from './icons'

export interface MobileNavItem {
  to: string
  label: string
  icon: string // emoji — usado pela nav atual (fallback, leve e "gameficado")
  /** Ícone SVG (launcher v2). Opcional p/ não quebrar nada que use só o emoji. */
  Icon?: ComponentType<IconProps>
  /** Selo no tile (ex.: "Novo"). */
  badge?: string
  adminOnly?: boolean
  /** Só aparece se cadastro_recebimento_enabled && !tiny_v3 (mesma regra do desktop). */
  cadastroGated?: boolean
}

// Fonte única dos itens — composta em TABS / MORE / GROUPS abaixo (sem duplicar dados).
// Sugestão foi consolidada no Miguel (sub-aba "Explorar") — o Miguel é o cockpit de compras.
const NAV = {
  painel:        { to: '/',                  label: 'Painel K',               icon: '🚀', Icon: IconGrid },
  miguel:        { to: '/miguel-comprador',  label: 'Comprador',              icon: '✨', Icon: IconSparkles },
  dashboard:     { to: '/estoque-dashboard', label: 'Dashboard',              icon: '🎯', Icon: IconChart },
  pedidos:       { to: '/historico-pedidos', label: 'Pedidos',                icon: '📦', Icon: IconPackage },
  emissao:       { to: '/relatorio-compras', label: 'Emissão de pedidos',     icon: '🧾', Icon: IconReceipt },
  abastecimento: { to: '/abastecimento-filial', label: 'Abastecimento Filial', icon: '🚚', Icon: IconTruck },
  cadastro:      { to: '/cadastro-recebimento', label: 'Cadastro e Recebimento', icon: '📥', Icon: IconInbox, cadastroGated: true },
  custo:         { to: '/custo-mercadoria',  label: 'Custo de Mercadoria',    icon: '🏷️', Icon: IconTag },
  inativos:      { to: '/inativos',          label: 'Inativos',               icon: '🗑️', Icon: IconArchive },
  fornecedor:    { to: '/fornecedor',        label: 'Fornecedor',             icon: '🤝', Icon: IconBuilding },
  config:        { to: '/configuracoes',     label: 'Configurações',          icon: '⚙️', Icon: IconSettings, adminOnly: true },
} satisfies Record<string, MobileNavItem>

/** Atalhos da barra inferior (alcance do polegar). Máx 4 + botão "Mais". */
export const MOBILE_TABS: MobileNavItem[] = [NAV.painel, NAV.miguel, NAV.dashboard, NAV.pedidos]

/** Aba "Início" (launcher v2) — entra na frente das MOBILE_TABS quando a flag liga. */
export const MOBILE_HOME_TAB: MobileNavItem = { to: '/inicio', label: 'Início', icon: '🏠', Icon: IconHome }

// No v2 a raiz "/" redireciona pro Início, então o Painel ganha rota própria.
// O fallback (flag OFF) continua usando NAV.painel em "/" — idêntico ao app antigo.
const painelV2: MobileNavItem = { ...NAV.painel, to: '/painel' }

/** Abas do launcher v2 (sem a Início, que entra na frente). */
export const MOBILE_TABS_V2: MobileNavItem[] = [painelV2, NAV.miguel, NAV.dashboard, NAV.pedidos]

/** Demais telas — abrem no bottom sheet "Mais". */
export const MOBILE_MORE: MobileNavItem[] = [
  NAV.emissao, NAV.abastecimento, NAV.cadastro, NAV.custo, NAV.inativos, NAV.fornecedor, NAV.config,
]

export interface MobileNavGroup {
  title: string
  items: MobileNavItem[]
}

/** Agrupamento do launcher v2 (Início + "Mais" repaginado). Cobre TODAS as seções. */
export const MOBILE_GROUPS: MobileNavGroup[] = [
  { title: 'Operação',     items: [painelV2, NAV.miguel, NAV.pedidos, NAV.emissao, NAV.abastecimento, NAV.cadastro] },
  { title: 'Análise & IA', items: [NAV.dashboard, NAV.custo] },
  { title: 'Gestão',       items: [NAV.fornecedor, NAV.inativos, NAV.config] },
]
